import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Crown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PeopleCards } from "@/components/landing/Members";
import { ProfileDialog } from "@/components/landing/ProfileDialog";
import { SubpageShell } from "@/components/layout/SubpageShell";

const EASE = [0.22, 1, 0.36, 1];

export default function LeadershipPage() {
  const { content, lang, loading, t } = useSite();
  const leaders = content?.leadership || [];
  const [selected, setSelected] = useState(null);

  if (loading || !content) {
    return (
      <div data-testid="leadership-loader" className="min-h-screen grid place-items-center bg-[#F8FAFC]">
        <div className="w-12 h-12 rounded-2xl bg-sky-400 animate-pulse" />
      </div>
    );
  }

  return (
    <SubpageShell titleZh="我们的管理层" titleEn="Meet Our Team">
    <div data-testid="leadership-page" className="noise relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-sky-200/50 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-orange-200/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-orange-500">
            <Crown size={14} />
            {lang === "zh" ? "管理层" : "Our Team"}
          </p>
          <h1 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-900">
            {lang === "zh" ? "我们的管理层" : "Meet Our Team"}
            <span className="text-orange-500">.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
            {lang === "zh"
              ? "带领团队一路走来的核心决策者，专业与热忱兼备。"
              : "The core decision-makers who lead the team forward — expertise meets passion."}
          </p>
        </motion.div>

        <PeopleCards items={leaders} testidPrefix="leader" onSelect={setSelected} />

        <ProfileDialog person={selected} onClose={() => setSelected(null)} testidPrefix="leader" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-20 text-center"
        >
          <a
            data-testid="leadership-cta-home"
            href="/"
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgb(249,115,22,0.35)] hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            <ArrowLeft size={16} />
            {lang === "zh" ? "回到首页了解更多" : "Back to Homepage"}
          </a>
        </motion.div>
      </div>
    </div>
    </SubpageShell>
  );
}
